import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { getFlowMaxStep } from "../flowConfig";

export const SetupContext = createContext(null);

const STORAGE_KEY = "@Korax:setupFlow";
const SETUP_FLOW_VERSION = 2;

const initialState = {
  currentStep: 0,
  completedSteps: [],
  company: {
    name: "",
    segment: "",
    teamSize: ""
  },
  whatsapp: {
    connected: false,
    connectionId: null
  },
  ai: {
    objective: "",
    trained: false,
    tested: false,
    promptId: null,
    paymentMethods: [],
    pixKeyType: "",
    pixKey: "",
    pixReceiverName: ""
  }
};

const loadStoredState = (companyId) => {
  try {
    if (!companyId) {
      return initialState;
    }

    const storedState = localStorage.getItem(STORAGE_KEY);

    if (!storedState) {
      return initialState;
    }

    const parsedState = JSON.parse(storedState);

    if (Number(parsedState?.companyId) !== Number(companyId)) {
      localStorage.removeItem(STORAGE_KEY);
      return initialState;
    }

    const storedWhatsapp = parsedState?.whatsapp || {};

    const validConnectionId =
      typeof storedWhatsapp.connectionId === "number" &&
      Number.isInteger(storedWhatsapp.connectionId);

    const storedVersion =
      Number(parsedState?.flowVersion || 0);

    const requiresFlowMigration =
      storedVersion !== SETUP_FLOW_VERSION;

    return {
      ...initialState,
      ...parsedState,
      currentStep: requiresFlowMigration
        ? 0
        : Number(parsedState?.currentStep || 0),
      completedSteps: requiresFlowMigration
        ? []
        : Array.isArray(parsedState?.completedSteps)
          ? parsedState.completedSteps
          : [],
      flowVersion: SETUP_FLOW_VERSION,
      whatsapp: validConnectionId
        ? {
            ...initialState.whatsapp,
            ...storedWhatsapp
          }
        : {
            ...initialState.whatsapp
          }
    };
  } catch (error) {
    return initialState;
  }
};

export const SetupProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const companyId = Number(user?.companyId || user?.company?.id || 0);

  const [setupState, setSetupState] = useState(() =>
    loadStoredState(companyId)
  );

  useEffect(() => {
    if (!companyId) {
      return;
    }

    setSetupState(loadStoredState(companyId));
  }, [companyId]);

  useEffect(() => {
    if (!companyId) {
      return;
    }

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...setupState,
        companyId,
        flowVersion: SETUP_FLOW_VERSION
      })
    );
  }, [setupState, companyId]);

  const updateSetup = useCallback((values) => {
    setSetupState((previousState) => ({
      ...previousState,
      ...values
    }));
  }, []);

  const updateCompany = useCallback((values) => {
    setSetupState((previousState) => ({
      ...previousState,
      company: {
        ...previousState.company,
        ...values
      }
    }));
  }, []);

  const updateWhatsapp = useCallback((values) => {
    setSetupState((previousState) => ({
      ...previousState,
      whatsapp: {
        ...previousState.whatsapp,
        ...values
      }
    }));
  }, []);

  const updateAI = useCallback((values) => {
    setSetupState((previousState) => ({
      ...previousState,
      ai: {
        ...previousState.ai,
        ...values
      }
    }));
  }, []);

  const goToStep = useCallback((step) => {
    setSetupState((previousState) => {
      const maxStep = getFlowMaxStep(
        previousState.ai?.objective
      );

      return {
        ...previousState,
        currentStep: Math.max(0, Math.min(step, maxStep))
      };
    });
  }, []);

  const nextStep = useCallback(() => {
    setSetupState((previousState) => {
      const completedSteps = previousState.completedSteps.includes(
        previousState.currentStep
      )
        ? previousState.completedSteps
        : [...previousState.completedSteps, previousState.currentStep];

      const maxStep = getFlowMaxStep(
        previousState.ai?.objective
      );

      return {
        ...previousState,
        currentStep: Math.min(
          previousState.currentStep + 1,
          maxStep
        ),
        completedSteps
      };
    });
  }, []);

  const previousStep = useCallback(() => {
    setSetupState((previousState) => ({
      ...previousState,
      currentStep: Math.max(previousState.currentStep - 1, 0)
    }));
  }, []);

  const resetSetup = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setSetupState(initialState);
  }, []);

  const value = useMemo(
    () => ({
      ...setupState,
      updateSetup,
      updateCompany,
      updateWhatsapp,
      updateAI,
      goToStep,
      nextStep,
      previousStep,
      resetSetup
    }),
    [
      setupState,
      updateSetup,
      updateCompany,
      updateWhatsapp,
      updateAI,
      goToStep,
      nextStep,
      previousStep,
      resetSetup
    ]
  );

  return (
    <SetupContext.Provider value={value}>
      {children}
    </SetupContext.Provider>
  );
};
